import * as machina from "machina";
import moment from "moment-timezone";
import { store } from "@/store/store";
import router from "@/router";
import { scrollTo } from "@/helpers/helpers";
import ScheduleScrollType from "@/constants/ScheduleScrollType";
import scrollDefinitions from "./scrollDefinitions";

/*
	NOTE: Keeping the commented-out console.log statements for debugging and PR-grok-ability.
*/

export default new machina.Fsm( {

	initialize() {
		this.lastScrollAction = { scrollType: null, targetSelector: null, };
		this.scrollTarget = null;
		this.navBackToSchedule = false;
		this.timeout = null;
		router.beforeEach( ( to, from, next ) => {
			const { path: pathTo, } = to;
			const { path: pathFrom, } = from;
			if ( pathTo !== pathFrom && !pathFrom.startsWith( "/schedule" ) && pathTo.startsWith( "/schedule" ) &&
				![ null, "/", "/callback", ].includes( pathFrom ) ) {
				this.navBackToSchedule = true;
			}
			next();
		} );
		this.unwatch = store.watch(
			state => {
				return {
					selectedScheduleDate: state.schedule.selectedScheduleDate,
					selectedScheduleMeetingId: state.schedule.selectedScheduleMeetingId,
				};
			},
			( newVal, oldVal ) => {
				const selectedScheduleDateChanged = !( oldVal.selectedScheduleDate && newVal.selectedScheduleDate && moment( oldVal.selectedScheduleDate ).isSame( moment( newVal.selectedScheduleDate ), "day" ) );
				const selectedScheduleMeetingIdChanged = oldVal.selectedScheduleMeetingId !== newVal.selectedScheduleMeetingId;

				const { selectedScheduleDate, } = newVal;
				// did we arrive here through a route change?
				// NOTE: might need an additional guard here once /schedule becomes the default route on login/signup
				if ( this.navBackToSchedule && this.lastScrollAction.targetSelector ) {
					return this.repeatLastScroll();
				}

				// Are we scrolling to either "today" or a specific date?
				if ( !selectedScheduleMeetingIdChanged && selectedScheduleDate && selectedScheduleDateChanged ) {
					this.scroll( selectedScheduleDate ? ScheduleScrollType.SELECTED_DATE : ScheduleScrollType.TODAY, selectedScheduleDate );
				}

				if ( selectedScheduleMeetingIdChanged ) {
					setImmediate( () => {
						this.scroll( ScheduleScrollType.SELECTED_MEETING );
						this.handle( "captureMeetingSelection" );
					} );
				}
			}
		);
	},

	initialState: "uninitialized",

	states: {
		/*
			The FSM starts in this state. We need the DOM reference to the Schedule component before we can proceed.
			If `scroll` is called while in this state, the input is deferred until we transition to `ready` state.
		*/
		uninitialized: {
			setDomRefence( ref ) {
				this.ref = ref;
				this.transition( "ready" );
			},
			scroll() {
				this.deferUntilTransition( "ready" );
				if ( this.ref ) {
					this.transition( "ready" );
				}
			},
		},
		/*
			In `ready` state, we look up the information we need to know how to scroll. If `scroll` was handled while
			in `uninitialized` state, then scroll will execute as soon as we've transitioned into `ready` state.
		*/
		ready: {
			scroll( scrollType, selector, ...args ) {
				this.scrollType = scrollType;
				const { targetSelector, } = scrollDefinitions[ this.scrollType ];
				this.targetSelector = selector || targetSelector( ...args );
				this.transition( "detecting" );
			},
			ignore: "ignoring",
		},
		/*
			In the `detecting` state, we wait until our scroll target appears in the DOM before we
			proceed any further. If we receive another scroll input while in this state, we cancel
			our polling of the DOM and we transition back to ready to switch to the new scroll input.
			This could happen, for example, if the user loaded the schedule page with a meeting id
			in the url, but then clicked on a different day of the week before the schedule list
			actually rendered.
		*/
		detecting: {
			_onEnter() {
				// Once we know the target selector, we need to wait for it to appear in the DOM
				const ensureSelector = selector => {
					this.scrollTarget = document.querySelector( selector );
					if ( !this.scrollTarget ) {
						this.timeout = setTimeout( () => ensureSelector( selector ), 1000 ); // eslint-disable-line no-magic-numbers
						return;
					}
					this.transition( "scrolling" );
				};
				ensureSelector( this.targetSelector );
			},
			scroll() {
				this.deferUntilTransition( "ready" );
				clearTimeout( this.timeout );
				this.timeout = null;
				this.transition( "ready" );
			},
		},
		/*
			In the `scrolling` state, we're finally getting to what we came for. We have the DOM reference,
			we know the selector we're interested, etc. - so we scroll to it.
		*/
		scrolling: {
			_onEnter() {
				const {
					toPosition,
					duration,
				} = scrollDefinitions[ this.scrollType ];
				scrollTo( this.ref, toPosition( this.scrollTarget ), duration );
				this.transition( "complete" );
			},
		},
		/*
			In the complete state, we store the metadata about the scroll action we just executed
			on the `lastScrollAction` property. This allows us to repeat that last scroll when
			navigating back to the schedule UI via browser back button, etc. We stay in this state
			unless the user initiates another scroll action (at which point we'll flip back to `ready`
			state and continue from there), or if the user navigates away from the Schedule and the
			component is torn down (at which point, we move into `disposing` state). While we're in
			this state, we listen for when the user clicks on a meeting, so that we can scroll to it
			if it's the last scroll action performed before navigating away from the Schedule and then
			back to the Schedule.
		*/
		complete: {
			_onEnter() {
				this.navBackToSchedule = false;
				this.lastScrollAction.scrollType = this.scrollType;
				this.lastScrollAction.targetSelector = this.targetSelector;
				this.scrollType = null;
				this.targetSelector = null;
			},

			scroll() {
				this.deferUntilTransition();
				this.transition( "ready" );
			},

			captureMeetingSelection() {
				this.lastScrollAction.scrollType = ScheduleScrollType.SELECTED_MEETING;
				this.lastScrollAction.targetSelector = scrollDefinitions[ ScheduleScrollType.SELECTED_MEETING ].targetSelector();
			},

			dispose: "disposing",
			ignore: "ignoring",
		},
		/*
			If we navigate away from the Schedule UI ,we need to ditch the DOM reference we had and then
			transition to `uninitialized`. If the user navigates back, then the Schedule component will
			init the FSM with a new DOM reference and we'll be good to go.
		*/
		disposing: {
			_onEnter() {
				this.ref = null;
				this.transition( "uninitialized" );
			},
		},
		ignoring: {
			resume: "ready",
		},
	},

	// These methods are here for 'expressiveness' and convenience. It reads better, in the calling code,
	// to call scrollMachine.scroll rather than scrollMachine.handle("scroll", scrollType, null, someDateValue):
	scroll( scrollType, ...args ) {
		this.handle( "scroll", scrollType, null, ...args );
	},

	repeatLastScroll() {
		this.handle(
			"scroll",
			this.lastScrollAction.scrollType,
			this.lastScrollAction.targetSelector
		);
	},

	initWithDomReference( ref ) {
		this.handle( "setDomRefence", ref );
	},

	dispose() {
		this.handle( "dispose" );
	},

	ignoreScrolling() {
		this.handle( "ignore" );
	},

	resumeScrolling() {
		this.handle( "resume" );
	},
} );



// WEBPACK FOOTER //
// ./src/helpers/scrollMachine.js